/**
 * @module @theme/wishlist-page
 *
 * Hydrates the /pages/wishlist page from localStorage.
 *
 * - Reads handles from window.Wishlist
 * - Fetches each product card HTML from /products/{handle}?view=card
 *   (a special view that wraps a single rendered <product-card>)
 * - Falls back to a JS-built card if the view is unavailable
 * - Reactively re-renders on `wishlist:update`
 * - Wires "Share" and "Clear all" actions
 */

import { Component } from '@theme/component';
import { wishlist, WishlistEvents } from '@theme/wishlist';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [subtitle]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [skeleton]
 * @property {HTMLElement} [grid]
 * @property {HTMLElement} [actions]
 * @property {HTMLButtonElement} [addAllButton]
 * @property {HTMLButtonElement} [shareButton]
 * @property {HTMLButtonElement} [clearButton]
 */

/** @extends {Component<Refs>} */
class WishlistPage extends Component {
  #renderToken = 0;
  #productCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.#render();
    document.addEventListener(WishlistEvents.update, this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(WishlistEvents.update, this.#onUpdate);
  }

  /* ── Public action methods (called from Liquid via on:click) ── */

  async share() {
    const button = /** @type {HTMLButtonElement|undefined} */ (this.refs.shareButton);
    if (!button) return;
    const url = wishlist.buildShareUrl();
    if (!url) return;

    const labels = (window.Theme && window.Theme.translations) || {};
    const copiedLabel = labels.wishlist_copied || 'Link copied';
    const originalLabel = button.dataset.originalLabel || button.textContent;

    const showCopied = () => {
      button.dataset.originalLabel = originalLabel;
      button.setAttribute('data-state', 'copied');
      button.textContent = copiedLabel;
      setTimeout(() => {
        button.removeAttribute('data-state');
        button.textContent = originalLabel || '';
      }, 1800);
    };

    /* Prefer the native share sheet when available (mobile). */
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
        showCopied();
        return;
      } catch (_error) {
        /* User cancelled — fall through to clipboard. */
      }
    }

    try {
      await navigator.clipboard.writeText(url);
    } catch (_error) {
      this.#fallbackCopy(url);
    }
    showCopied();
  }

  async addAllToCart() {
    const button = /** @type {HTMLButtonElement|undefined} */ (this.refs.addAllButton);
    const labels = (window.Theme && window.Theme.translations) || {};
    const original = button?.textContent || labels.wishlist_buy_all || 'Buy all';

    const rows = Array.from(this.querySelectorAll('[data-wl-buy-form]'));
    const grouped = new Map();

    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;
      const variantInput = row.querySelector('[data-wl-variant-id]');
      const addButton = row.querySelector('[data-wl-add]');
      if (!(variantInput instanceof HTMLInputElement)) continue;
      if (addButton instanceof HTMLButtonElement && addButton.disabled) continue;

      const variantId = Number(variantInput.value);
      if (!variantId) continue;
      grouped.set(variantId, (grouped.get(variantId) || 0) + 1);
    }

    const items = Array.from(grouped.entries()).map(([id, quantity]) => ({ id, quantity }));
    if (items.length === 0) {
      if (button) {
        button.textContent = labels.wishlist_no_available || 'No available variants';
        setTimeout(() => {
          button.textContent = original;
        }, 1300);
      }
      return;
    }

    if (button) {
      button.disabled = true;
      button.textContent = labels.wishlist_adding || 'Adding...';
    }

    try {
      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: 'header-group',
          sections_url: window.location.pathname,
        }),
      });

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            sourceId: 'wishlist-page',
            data: { source: 'wishlist-buy-all' },
          },
        })
      );

      if (button) {
        button.textContent = labels.wishlist_added_all || 'Added all';
      }
    } catch (_error) {
      if (button) {
        button.textContent = labels.wishlist_add_failed || 'Try again';
      }
    } finally {
      if (button) {
        setTimeout(() => {
          button.disabled = false;
          button.textContent = original;
        }, 1300);
      }
    }
  }

  clearAll() {
    if (wishlist.count() === 0) return;
    const labels = (window.Theme && window.Theme.translations) || {};
    const message = labels.wishlist_confirm_clear || 'Remove every item from your wishlist?';
    if (!window.confirm(message)) return;
    wishlist.clear();
  }

  /* ── Internals ───────────────────────────────────────────── */

  #onUpdate = () => this.#render();
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-wl-variant-select]')) return;
    const form = target.closest('[data-wl-buy-form]');
    if (!(form instanceof HTMLElement)) return;
    const variantInput = form.querySelector('[data-wl-variant-id]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = target.value;
    }
  };

  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-wl-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();

    const form = button.closest('[data-wl-buy-form]');
    if (!(form instanceof HTMLElement)) return;
    const variantInput = form.querySelector('[data-wl-variant-id]');
    const variantId = variantInput instanceof HTMLInputElement ? Number(variantInput.value) : 0;
    if (!variantId) return;

    const original = button.textContent || '';
    const labels = (window.Theme && window.Theme.translations) || {};
    button.disabled = true;
    button.textContent = labels.added || 'Added';

    try {
      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: 'header-group',
          sections_url: window.location.pathname,
        }),
      });

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            sourceId: 'wishlist-page',
            data: { source: 'wishlist' },
          },
        })
      );
    } catch (_error) {
      // Keep UX resilient: on error, route user to PDP where cart form is guaranteed.
      const fallbackHref = form.getAttribute('data-product-url');
      if (fallbackHref) window.location.href = fallbackHref;
    } finally {
      setTimeout(() => {
        button.disabled = false;
        button.textContent = original;
      }, 1200);
    }
  };

  /** @param {string} text */
  #fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); } catch (_e) { /* noop */ }
    document.body.removeChild(textarea);
  }

  async #render() {
    const token = ++this.#renderToken;
    const items = wishlist.items();
    const grid = /** @type {HTMLElement|undefined} */ (this.refs.grid);
    const empty = /** @type {HTMLElement|undefined} */ (this.refs.empty);
    const skeleton = /** @type {HTMLElement|undefined} */ (this.refs.skeleton);
    const actions = /** @type {HTMLElement|undefined} */ (this.refs.actions);

    if (!grid) return;

    if (items.length === 0) {
      this.#showState({ state: 'empty' });
      this.#updateSubtitle(0);
      grid.innerHTML = '';
      return;
    }

    /* Show skeleton only on first paint. */
    if (grid.children.length === 0) {
      this.#showState({ state: 'loading' });
    }

    /* Fetch in parallel; missing products simply fall out. */
    const results = await Promise.all(items.map((item) => this.#fetchCardMarkup(item.handle)));
    if (token !== this.#renderToken) return;

    const valid = results.filter((entry) => entry !== null);

    if (valid.length === 0) {
      this.#showState({ state: 'empty' });
      this.#updateSubtitle(0);
      grid.innerHTML = '';
      return;
    }

    this.#showState({ state: 'list' });
    this.#updateSubtitle(valid.length);

    /* Reuse existing nodes where possible to avoid flicker. */
    const existing = new Map();
    grid.querySelectorAll('[data-wishlist-card]').forEach((card) => {
      existing.set(card.getAttribute('data-handle'), card);
    });

    const fragment = document.createDocumentFragment();
    for (const entry of valid) {
      let card = existing.get(entry.handle);
      if (!card) {
        const tmp = document.createElement('div');
        tmp.innerHTML = entry.html;
        card = tmp.firstElementChild;
        if (!card) continue;
      } else {
        existing.delete(entry.handle);
      }
      fragment.appendChild(card);
    }

    /* Smoothly remove orphan cards that are no longer in the wishlist. */
    for (const [, node] of existing) {
      node.setAttribute('data-removing', '');
      setTimeout(() => node.remove(), 320);
    }

    grid.innerHTML = '';
    grid.appendChild(fragment);

    /* Reveal newly-attached cards. */
    requestAnimationFrame(() => {
      grid.querySelectorAll('[data-wishlist-card]:not([data-loaded])').forEach((card) => {
        card.setAttribute('data-loaded', '');
      });
    });
  }

  /**
   * @param {string} handle
   * @returns {Promise<{handle: string, html: string} | null>}
   */
  async #fetchCardMarkup(handle) {
    if (this.#productCache.has(handle)) {
      return { handle, html: this.#productCache.get(handle) };
    }

    /* Build a lightweight card from the product JSON. This is faster than
       parsing the full product page HTML, and the visual style is owned
       by the section stylesheet so the result still looks native. */
    const inner = await this.#buildCardFromJson(handle).catch(() => null);
    if (!inner) return null;

    const html = this.#wrapCard(handle, inner);
    this.#productCache.set(handle, html);
    return { handle, html };
  }

  /**
   * Builds a minimal product card matching the wishlist page's visual style.
   * @param {string} handle
   */
  async #buildCardFromJson(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const product = await response.json();

    const url = product.url || `/products/${product.handle}`;
    const img =
      resolveImageUrl(product.featured_image) ||
      resolveImageUrl(product.image) ||
      resolveImageUrl(product.images && product.images[0]) ||
      '';
    const variants = product.variants || [];
    const variant = variants.find((v) => v.available) || variants[0] || null;
    const priceCents = variant ? variant.price : product.price;
    const compareCents = variant ? variant.compare_at_price : product.compare_at_price;
    const onSale = (compareCents || 0) > (priceCents || 0);

    const moneyFormat = (window.Theme && window.Theme.routes && window.Theme.routes.money_format) || '${{amount}}';
    const formatMoney = (cents) => formatPrice(cents, moneyFormat);

    const imgHtml = img
      ? `<img class="product-card-fallback__image" loading="lazy" src="${esc(withWidth(img, 720))}" srcset="${esc(withWidth(img, 480))} 480w, ${esc(withWidth(img, 720))} 720w, ${esc(withWidth(img, 960))} 960w, ${esc(withWidth(img, 1200))} 1200w" sizes="(max-width: 749px) 50vw, 33vw" alt="${esc(product.title)}">`
      : '<span class="product-card-fallback__placeholder"></span>';

    const heartHtml = `
      <wishlist-button-component
        class="wishlist-button wishlist-button--card"
        data-handle="${esc(product.handle)}"
        data-product-id="${esc(product.id)}"
        aria-pressed="true"
        on:click="/toggle">
        <button type="button" class="wishlist-button__btn button-unstyled"
          aria-label="" data-label-add="" data-label-remove="" ref="button">
          <span class="wishlist-button__burst" aria-hidden="true"></span>
          <span class="wishlist-button__icon wishlist-button__icon--outline svg-wrapper" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path d="M10 17.25S3.25 13.06 3.25 8.5a3.75 3.75 0 0 1 6.75-2.25 3.75 3.75 0 0 1 6.75 2.25c0 4.56-6.75 8.75-6.75 8.75Z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="wishlist-button__icon wishlist-button__icon--filled svg-wrapper" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M10 17.25S3.25 13.06 3.25 8.5a3.75 3.75 0 0 1 6.75-2.25 3.75 3.75 0 0 1 6.75 2.25c0 4.56-6.75 8.75-6.75 8.75Z"/></svg>
          </span>
          <span class="visually-hidden" ref="srLabel" role="status"></span>
        </button>
      </wishlist-button-component>`;

    const priceHtml = onSale
      ? `<span class="product-card-fallback__price"><s class="product-card-fallback__price-was">${esc(formatMoney(compareCents))}</s> ${esc(formatMoney(priceCents))}</span>`
      : `<span class="product-card-fallback__price">${esc(formatMoney(priceCents))}</span>`;

    const labels = (window.Theme && window.Theme.translations) || {};
    const selectorHtml =
      variants.length > 1
        ? `<label class="product-card-fallback__variant-wrap">
            <span class="product-card-fallback__variant-label">${esc(labels.wishlist_size || 'Size')}</span>
            <select class="product-card-fallback__variant-select" data-wl-variant-select>
              ${variants
                .map((v) => {
                  const selected = variant && v.id === variant.id ? ' selected' : '';
                  const disabled = v.available ? '' : ' disabled';
                  const label = `${esc(v.title)}${v.available ? '' : ` — ${esc(labels.wishlist_sold_out || 'Sold out')}`}`;
                  return `<option value="${esc(v.id)}"${selected}${disabled}>${label}</option>`;
                })
                .join('')}
            </select>
          </label>`
        : '';

    const buyHtml = variant
      ? `<div class="product-card-fallback__actions" data-wl-buy-form data-product-url="${esc(url)}">
           <input type="hidden" data-wl-variant-id value="${esc(variant.id)}">
           ${selectorHtml}
           <button type="button" class="button ${variant.available ? 'button-primary' : 'button-secondary'} product-card-fallback__buy" data-wl-add${
             variant.available ? '' : ' disabled'
           }>
             ${variant.available ? esc(labels.add_to_cart || 'Add to cart') : esc(labels.wishlist_sold_out || 'Sold out')}
           </button>
         </div>`
      : '';

    return `
      <article class="product-card-fallback">
        <a class="product-card-fallback__link" href="${esc(url)}">
          <div class="product-card-fallback__media">
            ${imgHtml}
            ${heartHtml}
          </div>
          <div class="product-card-fallback__info">
            <h3 class="product-card-fallback__title">${esc(product.title)}</h3>
            ${priceHtml}
          </div>
        </a>
        ${buyHtml}
      </article>
    `;
  }

  /** @param {string} handle @param {string} inner */
  #wrapCard(handle, inner) {
    return `<div class="wishlist-grid__cell" data-wishlist-card data-handle="${esc(handle)}">${inner}</div>`;
  }

  /** @param {{state: 'empty'|'loading'|'list'}} param0 */
  #showState({ state }) {
    const empty = /** @type {HTMLElement|undefined} */ (this.refs.empty);
    const skeleton = /** @type {HTMLElement|undefined} */ (this.refs.skeleton);
    const grid = /** @type {HTMLElement|undefined} */ (this.refs.grid);
    const actions = /** @type {HTMLElement|undefined} */ (this.refs.actions);

    if (empty) empty.hidden = state !== 'empty';
    if (skeleton) skeleton.hidden = state !== 'loading';
    if (grid) grid.hidden = state !== 'list';
    if (actions) actions.hidden = state !== 'list';
  }

  /** @param {number} count */
  #updateSubtitle(count) {
    const sub = /** @type {HTMLElement|undefined} */ (this.refs.subtitle);
    if (!sub) return;
    if (count === 0) {
      sub.textContent = sub.dataset.empty || '';
      return;
    }
    const tpl = count === 1 ? sub.dataset.one : sub.dataset.many;
    sub.textContent = (tpl || '').replace(/\{\{\s*count\s*\}\}/g, String(count));
  }
}

if (!customElements.get('wishlist-page-component')) {
  customElements.define('wishlist-page-component', WishlistPage);
}

/* ── Helpers ─────────────────────────────────────────────────── */

/** @param {string} url @param {number} width */
function withWidth(url, width) {
  if (!url) return '';
  try {
    const normalized = String(url).startsWith('//') ? `https:${url}` : String(url);
    const u = new URL(normalized, window.location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch (_error) {
    return url;
  }
}

/**
 * Normalizes various Shopify image payload shapes to a plain URL string.
 * Accepts:
 * - "//cdn.../file.webp?v=..."
 * - "https://cdn.../file.webp?v=..."
 * - { src: "..."} / { url: "..."} / { featured_image: { ... } }
 *
 * @param {unknown} value
 * @returns {string}
 */
function resolveImageUrl(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const maybe = /** @type {any} */ (value);
    if (typeof maybe.src === 'string') return maybe.src;
    if (typeof maybe.url === 'string') return maybe.url;
    if (maybe.featured_image) return resolveImageUrl(maybe.featured_image);
  }
  return '';
}

/** @param {string} value */
function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => {
    return /** @type {Record<string,string>} */ ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c];
  });
}

/**
 * Resolves Shopify shop.money_format placeholders against a numeric value.
 * @param {number} cents
 * @param {string} format
 */
function formatPrice(cents, format) {
  const TOKEN_RE = new RegExp('\\{\\{\\s*([a-z_]+)\\s*\\}\\}', 'g');
  return format.replace(TOKEN_RE, (_, key) => {
    switch (key) {
      case 'amount':
        return (cents / 100).toFixed(2);
      case 'amount_no_decimals':
        return Math.round(cents / 100).toString();
      case 'amount_with_comma_separator':
        return (cents / 100).toFixed(2).replace('.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return Math.round(cents / 100).toLocaleString('de-DE');
      case 'amount_with_space_separator':
        return (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      default:
        return '';
    }
  });
}
